// ============================================================================
// apps/explorerRenderer.tsx — ПРИЛОЖЕНИЕ «Файлы» (Explorer, appKind='explorer').
// Файловый менеджер базы: ВСЕ файлы (sdk.files.list без owner), поиск по имени,
// загрузка/скачивание/удаление, превью через ContentViewer (реестр обработчиков:
// xlsx/csv/img/pdf/docx/текст — ParseContent + детектор кодировок).
//
// АРХИТЕКТУРА (AppSDK): работает ТОЛЬКО через проп `sdk` (функции ядра с
// проверкой scope манифеста) — НЕ через глобальный getSDK(). Это изоляция прав
// (permissions), эталон для сторонних приложений и переиспользуемость.
// Манифест EXPLORER_APP_MANIFEST заявляет нужные скоупы явно.
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Search, Upload, RefreshCw, MessageCircle, LayoutGrid, List, Table2 } from 'lucide-react';
import { registerAppRenderer, type AppSdk, type AppManifest, APPSDK_VERSION } from '@/lib/apps/sdk';
import { unwrap, errorText } from '@/lib/appSdkUtils';
import { eventBusHub } from '@/lib/eventBusHub';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { confirmDialog } from '@/lib/dialogs';
import { ContentViewer } from '@/components/viewer/ContentViewer';
import { FileCollection, type FileCollectionView, type FileDetail } from '@/components/collection/FileCollection';

/**
 * ExplorerApp — файловый менеджер базы (переиспользуется приложением
 * «Файлы» и вкладкой «Файлы» кабинета; работает через переданный AppSDK).
 */
export function ExplorerApp({ sdk }: { sdk: AppSdk }) {
  const [files, setFiles] = useState<FileDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<FileDetail | null>(null);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState<FileCollectionView>(() =>
    (localStorage.getItem('hydra_explorer_view') as FileCollectionView) || 'table',
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      type SdkFile = { id: string; fileName: string; fileType?: string; fileSize?: string; uploadedBy?: string; uploadedAt?: string };
      const res = await unwrap<SdkFile[]>(sdk.files.list(''), (err) => sdk.notify.toast(err, 'error'));
      if (!res) return; // ошибка уже показана
      setFiles(res.map((f) => ({
        id: String(f.id ?? ''),
        fileName: String(f.fileName ?? ''),
        fileSize: Number(f.fileSize ?? 0),
        fileType: String(f.fileType ?? ''),
        uploadedBy: f.uploadedBy,
        uploadedAt: f.uploadedAt,
      })));
    } catch (e) {
      sdk.notify.toast(errorText(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => { void load(); }, [load]);

  const filtered = files.filter((f) => !search || f.fileName.toLowerCase().includes(search.toLowerCase()));

  const openUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const ok = await unwrap(sdk.files.upload(file.name, file, { fileType: file.type || '' }), (err) => sdk.notify.toast(err, 'error'));
        if (ok === null) return;
        sdk.notify.toast('Файл загружен', 'success');
        await load();
      } catch (e) {
        sdk.notify.toast(errorText(e), 'error');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const removeFile = async (f: FileDetail) => {
    if (!await confirmDialog(`Удалить файл «${f.fileName}»?`)) return;
    const ok = await unwrap(sdk.files.removeFile(f.id), (err) => sdk.notify.toast(err, 'error'));
    if (ok === null) return;
    await load();
  };

  // discussFile — открыть обсуждение файла (контекстный чат с ассистентом).
  const discussFile = (f: FileDetail) => {
    eventBusHub.emit('ui.open-context-chat', {
      objectType: 'file',
      objectCode: 'file',
      objectId: f.id,
      title: f.fileName,
    });
  };

  const setViewPersist = (v: FileCollectionView) => {
    setView(v);
    localStorage.setItem('hydra_explorer_view', v);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Поиск файла…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setViewPersist('table')} title="Таблица" className={view === 'table' ? 'text-primary' : ''}><Table2 className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setViewPersist('list')} title="Список" className={view === 'list' ? 'text-primary' : ''}><List className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setViewPersist('card')} title="Карточки" className={view === 'card' ? 'text-primary' : ''}><LayoutGrid className="h-4 w-4" /></Button>
        </div>
        <div className="ml-1 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}><RefreshCw className="mr-1 h-4 w-4" />Обновить</Button>
          <Button size="sm" onClick={openUpload} disabled={uploading}><Upload className="mr-1 h-4 w-4" />Загрузить</Button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-2">
        <FileCollection
          files={filtered}
          view={view}
          settings={{ columns: ['name', 'size', 'uploaded_by', 'uploaded_at'], cardFields: ['name', 'size', 'uploaded_by', 'uploaded_at'] }}
          onPick={(f) => setPreview(f)}
          onRemove={(f) => void removeFile(f)}
          actions
          emptyText={loading ? 'Загрузка…' : 'Файлов нет. Загрузите первый файл.'}
        />
        {/* Обсуждение доступно из карточки/таблицы через onPick → превью */}
      </div>
      {preview && (
        <div className="fixed inset-0 z-[15000] flex items-center justify-center bg-black/40 p-4" onClick={() => setPreview(null)}>
          <div className="flex h-[80vh] w-[min(880px,94vw)] flex-col rounded-lg border bg-background" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="truncate text-sm font-medium">{preview.fileName}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => { discussFile(preview); setPreview(null); }}>
                  <MessageCircle className="mr-1 h-4 w-4" />Обсудить
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>✕</Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 p-3">
              <ContentViewer fileId={preview.id} fileName={preview.fileName} height="100%" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Код системного приложения «Файлы» (kind='app' code='explorer'). */
export const EXPLORER_APP_CODE = 'explorer';

/** Манифест приложения «Файлы» (системное, но permissions ЯВНЫЕ — эталон). */
export const EXPLORER_APP_MANIFEST: AppManifest = {
  code: EXPLORER_APP_CODE,
  name: 'explorer',
  synonym: 'Файлы',
  appKind: 'explorer',
  area: 'full',
  apiVersion: APPSDK_VERSION,
  permissions: ['files', 'notify', 'events', 'nav', 'data:read'],
};

export function registerExplorerApp(): void {
  registerAppRenderer({
    appKind: 'explorer',
    code: EXPLORER_APP_CODE,
    title: 'Файлы',
    Component: ExplorerApp,
    manifest: EXPLORER_APP_MANIFEST,
  });
}

